import {
  ProductCard as UIProductCard,
  ProductCardContent as UIProductCardContent,
  ProductCardImage as UIProductCardImage,
} from '@faststore/ui'
import type { PropsWithChildren } from 'react'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useBraindwSendGA } from 'src/components/hooks/useBraindwSendGA'
import { useGetMulticurrencyPrices } from 'src/components/hooks/useGetMulticurrencyPrices'
import ProductCardSkeleton from 'src/components/skeletons/ProductCardSkeleton'
import { Badge, DiscountBadge } from 'src/components/ui/Badge'
import BuyButton from 'src/components/ui/BuyButton'
import { ShopIcon } from 'src/components/ui/Icons/ShopIcon'
import { Image } from 'src/components/ui/Image'
import Link from 'src/components/ui/Link'
import Price from 'src/components/ui/Price'
import { ProductMKTPrice } from 'src/components/ui/Price/productMKTPrice'
import { QuantitySelector } from 'src/components/ui/QuantitySelector/QuantitySelector'
import { PRICE_TYPES } from 'src/constants'
import { cartStore, useCart } from 'src/sdk/cart'
import { useCartItemEvent } from 'src/sdk/cart/useCartItemEvent'
import { useDiscountPercent } from 'src/sdk/product/useDiscountPercent'
import { useFormattedPrice } from 'src/sdk/product/useFormattedPrice'
import { useProduct } from 'src/sdk/product/useProduct'
import { useProductLink } from 'src/sdk/product/useProductLink'
import isProductMKT from 'src/utils/productMKTChecker'

import styles from './product-card.module.scss'

const ShowBuyButton = ({
  hasVariations,
  linkProps,
  children,
}: PropsWithChildren<TBuyButton>) => (
  <>{hasVariations ? <Link {...linkProps}>{children}</Link> : children}</>
)

const ProductCardImage = ({
  coleccion,
  linkBraindw,
  variantName,
  sendEventGA,
  itemValues,
  eventAction,
  productImages,
}: any) => {
  return (
    <div data-fs-product-image-content>
      <UIProductCardImage>
        <div className={`global-shelf__flags flag ${coleccion}`} />
        <Link
          {...linkBraindw}
          aria-label={`Link to ${variantName}`}
          title={variantName}
          onClick={() => {
            sendEventGA(itemValues, eventAction, 'clickEvent')
          }}
        >
          <Image
            src={productImages?.[0]?.url}
            alt={productImages?.[0]?.alternateName}
            width={360}
            height={360}
            sizes="(max-width: 768px) 25vw, 30vw"
            loading="lazy"
          />
        </Link>
      </UIProductCardImage>
    </div>
  )
}

const ProductCardPrice = ({
  realPrice,
  listPrice,
  measurementUnit,
  spotPriceKg,
  sellerIdentifier,
  memoMulticurrecySellingPrice,
  memoMulticurrecyListPrice,
  memoMulticurrencyLoading,
}: any) => {
  return (
    <>
      <div data-fs-product-card-prices className="product-shelf__prices">
        {(realPrice < listPrice && !isProductMKT(sellerIdentifier)) ||
        (realPrice < listPrice &&
          isProductMKT(sellerIdentifier) &&
          memoMulticurrecySellingPrice <= 0) ? (
          <Price
            value={listPrice}
            formatter={useFormattedPrice}
            testId="list-price"
            data-value={listPrice}
            variant="listing"
            classes="text__legend"
            SRText="Original price:"
          />
        ) : null}

        {!isProductMKT(sellerIdentifier) ||
        (isProductMKT(sellerIdentifier) &&
          memoMulticurrecySellingPrice <= 0) ? (
          <Price
            value={realPrice}
            formatter={useFormattedPrice}
            testId="price"
            data-value={realPrice}
            variant="spot"
            classes="text__body"
            SRText="Sale Price:"
          />
        ) : null}

        {memoMulticurrecyListPrice && isProductMKT(sellerIdentifier) ? (
          <ProductMKTPrice
            priceValue={memoMulticurrecyListPrice}
            priceType={PRICE_TYPES.listPrice}
            componentToRender="productDetails"
          />
        ) : null}

        {!memoMulticurrencyLoading &&
        memoMulticurrecySellingPrice &&
        memoMulticurrecySellingPrice > 0 &&
        isProductMKT(sellerIdentifier) ? (
          <ProductMKTPrice
            priceValue={memoMulticurrecySellingPrice}
            priceType={PRICE_TYPES.sellingPrice}
            componentToRender="suggestionProductCard"
          />
        ) : null}
      </div>
      {measurementUnit === 'kg' && !isProductMKT(sellerIdentifier) && (
        <div data-fs-product-card-price-unit>
          <p data-fs-product-card-price-unit>&#40;</p>
          <Price
            value={spotPriceKg}
            formatter={useFormattedPrice}
            classes="text_price_unit_card"
            SRText="Sale Price:"
          />
          <p className="text_price_unit_card">x {measurementUnit}</p>
          <p className="text_price_unit_card">&#41;</p>
        </div>
      )}
    </>
  )
}

const ProductCardBadge = ({ outOfStock, listPrice, lowPrice }: any) => {
  const discountPercent = useDiscountPercent(listPrice, lowPrice)

  return (
    <>
      {outOfStock ? (
        <Badge>Out of stock</Badge>
      ) : (
        <DiscountBadge discountPercent={discountPercent} />
      )}
    </>
  )
}

const ProductCardBuyButton = ({
  hasVariations,
  linkProps,
  handleBuyButton,
  buyButtonText,
}: any) => {
  return (
    <div data-fs-button-container className="buy-button__container">
      <ShowBuyButton hasVariations={hasVariations} linkProps={linkProps}>
        <BuyButton
          className="buy-button"
          onClick={() => handleBuyButton()}
          hasIcon={!hasVariations}
        >
          {buyButtonText}
        </BuyButton>
      </ShowBuyButton>
    </div>
  )
}

function ProductCardBraindw({ product, eventAction }: any) {
  const { sku: productId } = product
  const { sendEventGA } = useBraindwSendGA()

  const { data, isValidating } = useProduct(productId, {
    product,
  })

  if (!data) {
    throw new Error('NotFound')
  }

  const {
    product: {
      id,
      slug,
      sku,
      clusterHighlights,
      name: variantName,
      brand,
      isVariantOf,
      image: productImages,
      offers: { offers, lowPrice },
      unitMultiplier,
      measurementUnit,
      gtin,
      additionalProperty,
    },
  } = data

  const { items } = useCart()
  const { addItem, removeItem, updateItemQuantity } = cartStore
  const [quantityItems, setQuantityItems] = useState(0)

  const variationsTalle = isVariantOf?.skuVariants?.availableVariations?.Talle
  const hasVariations = variationsTalle && variationsTalle.length > 0

  const cartProduct = items?.find(
    (item) => item?.itemOffered?.sku === product.sku
  )

  const { sendCartItemEvent } = useCartItemEvent()

  const onQuantityChange = useCallback(
    (quantity: number) => {
      cartProduct && sendCartItemEvent(cartProduct, quantity)
    },
    [cartProduct, sendCartItemEvent]
  )

  const linkProps = useProductLink({
    product,
    selectedOffer: 0,
    index: 0,
    slug,
  })

  const linkBraindw = {
    ...linkProps,
    href: `${linkProps.href}?isbraindw=${eventAction}`,
  }

  const outOfStock =
    offers && offers[0]?.availability !== 'https://schema.org/InStock'

  const listPrice = offers?.[0]?.listPrice
  const seller = offers?.[0]?.seller
  const sellerIdentifier = offers?.[0]?.seller?.identifier
  const sellerDefault = '1'

  const [coleccion, setColeccion] = useState('')

  const {
    loading: multicurrencyLoading,
    multicurrecySellingPrice,
    multicurrencyListPrice,
  } = useGetMulticurrencyPrices(sellerIdentifier, sku)

  const memoizedPrices = useMemo(() => {
    return {
      multicurrecySellingPrice,
      multicurrencyListPrice,
      multicurrencyLoading,
    }
  }, [multicurrecySellingPrice, multicurrencyListPrice, multicurrencyLoading])

  const memoMulticurrecySellingPrice = memoizedPrices.multicurrecySellingPrice
  const memoMulticurrecyListPrice = memoizedPrices.multicurrencyListPrice
  const memoMulticurrencyLoading = memoizedPrices.multicurrencyLoading

  useEffect(() => {
    if (!clusterHighlights.length || memoMulticurrencyLoading) {
      return
    }

    const coleccionText = clusterHighlights?.map(
      (item: { name: string }, index: number) =>
        index === 0 ? `${item.name}` : `${coleccionText} ${item.name}`
    )

    setColeccion(coleccionText)
  }, [clusterHighlights, memoMulticurrencyLoading])

  const buyButtonText = hasVariations ? 'COMPRAR' : 'Agregar'

  let spotPriceKg = lowPrice
  let realPrice = lowPrice

  if (unitMultiplier) {
    spotPriceKg =
      unitMultiplier <= 1 ? (1 / unitMultiplier) * lowPrice : lowPrice

    realPrice = unitMultiplier > 1 ? lowPrice * unitMultiplier : lowPrice
  }

  const itemValues = {
    id,
    quantity: 1,
    price: realPrice,
    listPrice,
    seller,
    itemOffered: {
      image: productImages?.map((img: any) => ({
        url: img?.url,
        alternateName: img?.alternateName,
      })),
      additionalProperty,
      sku,
      name: variantName,
      gtin,
      brand: {
        name: brand?.name,
      },
      isVariantOf: {
        productGroupID: isVariantOf?.productGroupID,
        name: isVariantOf?.name,
      },
    },
  }

  const handleSubmitItem = () => {
    const quantityDelta = 2

    sendCartItemEvent(itemValues, quantityDelta)
    setQuantityItems(1)
    addItem(itemValues)
    sendEventGA(itemValues, eventAction, 'addToCartBraindw')
  }

  const handleBuyButton = (): void => {
    !hasVariations && handleSubmitItem()
  }

  if (isValidating) {
    return <ProductCardSkeleton sectioned />
  }

  const productImageProps = {
    coleccion,
    linkBraindw,
    variantName,
    sendEventGA,
    itemValues,
    eventAction,
    productImages,
  }

  const productPriceProps = {
    realPrice,
    listPrice,
    measurementUnit,
    spotPriceKg,
    sellerIdentifier,
    memoMulticurrencyLoading,
    memoMulticurrecySellingPrice,
    memoMulticurrecyListPrice,
  }

  const productBuyButtonProps = {
    hasVariations,
    linkProps,
    handleBuyButton,
    buyButtonText,
  }

  return (
    <UIProductCard
      data-fs-product-card
      data-fs-product-card-sku={sku}
      className={`${styles.fsProductCard}`}
    >
      <ProductCardImage {...productImageProps} />
      <UIProductCardContent data-fs-product-card-content>
        <div data-fs-product-card-heading>
          <h3 data-fs-product-card-title>
            <Link
              {...linkBraindw}
              aria-label={`Link to ${variantName}`}
              title={variantName}
              onClick={() => {
                sendEventGA(itemValues, eventAction, 'clickEvent')
              }}
            >
              {variantName}
            </Link>
          </h3>
          <div data-fs-product-card-brand className="product__shelf__brand">
            <p className="product-shelf__brand-text">{brand?.name}</p>
            {sellerIdentifier !== sellerDefault && <ShopIcon />}
          </div>
          <ProductCardPrice {...productPriceProps} />
        </div>
        <ProductCardBadge
          outOfStock={outOfStock}
          listPrice={listPrice}
          lowPrice={lowPrice}
        />
        {!cartProduct?.id ? (
          <ProductCardBuyButton {...productBuyButtonProps} />
        ) : (
          <div
            data-fs-button-container
            className="quantity-selector__container"
          >
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <p
                style={{
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: '#00337B',
                  width: '102px',
                  whiteSpace: 'nowrap',
                }}
              >
                Agregado al carrito
              </p>
            </div>
            <QuantitySelector
              cartQuantity={cartProduct?.quantity}
              quantity={quantityItems}
              setQuantity={setQuantityItems}
              updateItems={updateItemQuantity}
              removeItem={removeItem}
              productId={cartProduct?.id}
              onChange={onQuantityChange}
            />
          </div>
        )}
      </UIProductCardContent>
    </UIProductCard>
  )
}

export default memo(ProductCardBraindw)
